import React, {useEffect, useMemo, useState} from 'react';
import {useProjectData} from "../../hooks/useProjectData"
import ProjectPageHeader from "../layouts/ProjectPageHeader"
import {Box, CircularProgress, Typography} from "@mui/material"
import {DataGrid, GridCellParams, GridColDef, GridRenderCellParams} from "@mui/x-data-grid"
import {useNavigate} from "react-router-dom"
import ApiError from "../../api/ApiError"
import {AnyTemplateJson} from "../../api/json/TemplateJson"
import {TemplateRequests} from "../../api/requests/TemplateRequests"
import {TimeUtil} from "../../library/TimeUtil"
import TemplateActionsMenu from "./TemplateActionsMenu"
import TemplateRenderer from "./TemplateRenderer"
import ErrorMessage from "../error/ErrorMessage"
import {useBreadcrumbs} from "../layouts/Nav/BreadcrumbsProvider"
import {NAV_LABELS} from "../../library/NavLabels"

function TemplatesPage() {
    useBreadcrumbs([{label: NAV_LABELS.TEMPLATES}])
    const {id, project} = useProjectData()

    const [templates, setTemplates] = useState<AnyTemplateJson[]>()
    const [templatesLoading, setTemplatesLoading] = useState(false)
    const [loadingError, setLoadingError] = useState<ApiError>()

    const navigate = useNavigate()

    useEffect(() => {
        if (templatesLoading || templates) return

        load()
    }, [project])

    const load = () => {
        setTemplatesLoading(true)
        TemplateRequests.list(id)
            .then(response => {
                setTemplates(response.content)
                setLoadingError(undefined)
            })
            .catch(setLoadingError)
            .finally(() => setTemplatesLoading(false))
    }

    const columns: GridColDef<AnyTemplateJson>[] = useMemo(() => [
        {
            field: 'name',
            headerName: 'Name',
            minWidth: 180,
            flex: 0.5,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams<AnyTemplateJson, string>) => (
                <Typography variant="body2">{params.row.name}</Typography>
            ),
        },
        {
            field: 'preview',
            headerName: 'Preview',
            flex: 1,
            minWidth: 240,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            valueGetter: () => '',
            renderCell: (params: GridRenderCellParams<AnyTemplateJson>) => (
                <Box sx={{
                    maxHeight: '100%',
                    overflow: 'hidden',
                    fontSize: 12,
                    color: 'text.secondary',
                    py: 0.5,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    width: '100%',
                }}>
                    <TemplateRenderer templateParts={params.row} staticSystemPromptTreatment="show"/>
                </Box>
            ),
        },
        {
            field: 'models',
            headerName: 'Models',
            width: 90,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: 'right',
            headerAlign: 'right',
            valueGetter: (_value, row: AnyTemplateJson) =>
                row.stats?.numberOfCustomModels ?? 0,
            renderCell: (params: GridRenderCellParams<AnyTemplateJson, number>) => (
                <Typography variant="body2">{params.value || ""}</Typography>
            ),
        },
        {
            field: 'updated',
            headerName: 'Updated',
            width: 130,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            valueGetter: (_value, row: AnyTemplateJson) => row.updated,
            renderCell: (params: GridRenderCellParams<AnyTemplateJson, string>) => (
                <Typography variant="body2" color="text.secondary">
                    {TimeUtil.ago(params.value ?? "")}
                </Typography>
            ),
        },
        {
            field: '__actions__',
            headerName: '',
            width: 56,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<AnyTemplateJson>) => (
                project && (
                    <TemplateActionsMenu project={project}
                                         template={params.row}
                                         didRename={(renamed) => {
                                             if (!templates) return
                                             const index = templates.findIndex(t => t.id === renamed.id)
                                             if (index === -1) {
                                                 load()
                                             } else {
                                                 const updated = [...templates]
                                                 updated[index].name = renamed.name
                                                 setTemplates(updated)
                                             }
                                         }}
                                         didCopy={(copy) => setTemplates([copy, ...(templates ?? [])])}
                                         didDelete={() => {
                                             if (!templates) return
                                             const index = templates.findIndex(t => t.id === params.row.id)
                                             if (index !== -1) {
                                                 const updated = [...templates]
                                                 updated.splice(index, 1)
                                                 setTemplates(updated)
                                             }
                                         }}
                    />
                )
            ),
        },
    ], [id, project, templates])

    if (!project) {
        return <CircularProgress/>
    }

    return (
        <>
            <ProjectPageHeader
                showFab
                onFabClick={() => navigate(`/projects/${id}/templates/new`)}
                pageTitle="Templates"
            />

            {loadingError && <ErrorMessage error={loadingError}/>}

            {templates &&
                <Box sx={{width: '100%'}}>
                    <DataGrid
                        autoHeight
                        rows={templates}
                        columns={columns}
                        getRowId={(row) => row.id}
                        getRowHeight={() => 80}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        hideFooter
                        loading={templatesLoading}
                        onCellClick={(params: GridCellParams) => {
                            if (params.field === '__actions__') return
                            navigate(`/projects/${id}/templates/${params.row.id}`)
                        }}
                        sx={{
                            backgroundColor: 'white',
                            '& .MuiDataGrid-row': {cursor: 'pointer'},
                            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                                outline: 'none',
                            },
                            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                                outline: 'none',
                            },
                        }}
                    />
                </Box>
            }
        </>
    );
}

export default TemplatesPage;
